import { buildApiUrl } from '../utils/apiConfig';
import type { PageResponse } from './polarisService';

export type FeatureIdeaStatus = 'ACTIVE' | 'COMPLETED';
export type VoteType = 'UP' | 'DOWN';

export interface FeatureIdeaSummary {
    id: number;
    title: string;
    description: string;
    status: FeatureIdeaStatus;
    voteScore: number;
    currentUserVote: VoteType | null;
    commentCount: number;
    authorDisplayName: string;
    authorCompanyName: string;
    authorAvatarUrl: string | null;
    createdAt: string;
    completedAt: string | null;
}

export interface FeatureIdeaComment {
    id: number;
    body: string;
    authorDisplayName: string;
    authorCompanyName: string;
    authorAvatarUrl: string | null;
    createdAt: string;
}

export interface FeatureIdeaDetail extends Omit<FeatureIdeaSummary, 'commentCount'> {
    comments: PageResponse<FeatureIdeaComment>;
}

export interface FeatureIdeaVoteResult {
    ideaId: number;
    voteScore: number;
    currentUserVote: VoteType | null;
}

const defaultHeaders = { 'Content-Type': 'application/json' };
const defaultOptions: RequestInit = { credentials: 'include', headers: defaultHeaders };

async function parseError(res: Response, fallback: string): Promise<string> {
    try {
        const data = await res.json();
        return data?.detail || data?.message || data?.title || fallback;
    } catch {
        return fallback;
    }
}

export async function fetchFeatureIdeas(
    status: FeatureIdeaStatus,
    page = 0,
    size = 10,
): Promise<PageResponse<FeatureIdeaSummary>> {
    const url = buildApiUrl(`api/feature-ideas?status=${status}&page=${page}&size=${size}`);
    const res = await fetch(url, defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load feature ideas.'));
    return res.json();
}

export async function fetchFeatureIdea(id: number): Promise<FeatureIdeaDetail> {
    const res = await fetch(buildApiUrl(`api/feature-ideas/${id}`), defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load feature idea.'));
    return res.json();
}

export async function createFeatureIdea(title: string, description: string): Promise<FeatureIdeaSummary> {
    const res = await fetch(buildApiUrl('api/feature-ideas'), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify({ title, description }),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to submit idea.'));
    return res.json();
}

export async function castFeatureIdeaVote(id: number, voteType: VoteType): Promise<FeatureIdeaVoteResult> {
    const res = await fetch(buildApiUrl(`api/feature-ideas/${id}/vote`), {
        ...defaultOptions,
        method: 'PUT',
        body: JSON.stringify({ voteType }),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to cast vote.'));
    return res.json();
}

export async function fetchFeatureIdeaComments(
    id: number,
    page = 0,
    size = 50,
): Promise<PageResponse<FeatureIdeaComment>> {
    const res = await fetch(buildApiUrl(`api/feature-ideas/${id}/comments?page=${page}&size=${size}`), defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load comments.'));
    return res.json();
}

export async function addFeatureIdeaComment(id: number, body: string): Promise<FeatureIdeaComment> {
    const res = await fetch(buildApiUrl(`api/feature-ideas/${id}/comments`), {
        ...defaultOptions,
        method: 'POST',
        body: JSON.stringify({ body }),
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to add comment.'));
    return res.json();
}

export async function fetchPolarisFeatureIdeas(
    status: FeatureIdeaStatus | undefined,
    page = 0,
    size = 20,
): Promise<PageResponse<FeatureIdeaSummary>> {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (status) params.set('status', status);
    const res = await fetch(buildApiUrl(`api/polaris/feature-ideas?${params}`), defaultOptions);
    if (!res.ok) throw new Error(await parseError(res, 'Failed to load feature ideas.'));
    return res.json();
}

export async function markPolarisFeatureIdeaComplete(id: number): Promise<FeatureIdeaSummary> {
    const res = await fetch(buildApiUrl(`api/polaris/feature-ideas/${id}/complete`), {
        ...defaultOptions,
        method: 'PATCH',
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to mark idea complete.'));
    return res.json();
}

export async function reopenPolarisFeatureIdea(id: number): Promise<FeatureIdeaSummary> {
    const res = await fetch(buildApiUrl(`api/polaris/feature-ideas/${id}/reopen`), {
        ...defaultOptions,
        method: 'PATCH',
    });
    if (!res.ok) throw new Error(await parseError(res, 'Failed to reopen idea.'));
    return res.json();
}

export function formatRelativeTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const seconds = Math.round((date.getTime() - Date.now()) / 1000);
    const abs = Math.abs(seconds);
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (abs < 60) return rtf.format(seconds, 'second');
    const minutes = Math.round(seconds / 60);
    if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
    const days = Math.round(hours / 24);
    if (Math.abs(days) < 30) return rtf.format(days, 'day');
    const months = Math.round(days / 30);
    if (Math.abs(months) < 12) return rtf.format(months, 'month');
    return rtf.format(Math.round(months / 12), 'year');
}
